import { ApolloClient, ApolloLink, FetchPolicy, from, HttpLink, NormalizedCacheObject } from '@apollo/client';
import { onError } from '@apollo/client/link/error';
import { OperationDefinitionNode } from 'graphql';

import typeDefs from '@/graphql/typeDefs/schema.graphql';
import { apiUrl } from '@/services/config';
import { addErrorToTracker } from '@/services/datadog';

import cache from './cache';

const APOLLO_CLIENT_DEFAULT_OPTIONS = {
	name: 'dev-console',
	version: '1.0',
};

// Add performance monitoring link
const performanceLink = new ApolloLink((operation, forward) => {
	const startTime = performance.now();
	return forward(operation).map((response) => {
		const endTime = performance.now();
		const duration = endTime - startTime;
		if (duration > 1000) {
			// Log slow requests (>1s)
			console.warn(`Slow GraphQL request: ${operation.operationName} took ${duration.toFixed(2)}ms`);
		}
		return response;
	});
});

// Add request deduplication link
const deduplicationLink = new ApolloLink((operation, forward) => {
	const operationDef = operation.query.definitions[0] as OperationDefinitionNode;
	if (operationDef.operation === 'query') {
		const operationName = operation.operationName;
		const variables = JSON.stringify(operation.variables);
		const requestKey = `${operationName}-${variables}`;

		// Check if we have a pending request with the same key
		const pendingRequest = activeRequests.get(requestKey);
		if (pendingRequest && !pendingRequest.controller.signal.aborted) {
			// Return the existing request's promise
			return pendingRequest.promise;
		}
	}
	return forward(operation);
});

const httpLink = new HttpLink({
	uri: apiUrl,
	credentials: 'same-origin',
	fetchOptions: {
		mode: 'cors',
	},
});

const errorLink = onError(({ graphQLErrors = [], networkError, operation }) => {
	if (graphQLErrors.length > 0) {
		addErrorToTracker(graphQLErrors, 'network');
	} else if (networkError) {
		addErrorToTracker(graphQLErrors.length > 0 ? graphQLErrors : networkError, 'network');
	}
});

// Create a map to store active requests
const activeRequests = new Map();

// Function to abort all active requests
const abortAllRequests = () => {
	for (const [key, request] of activeRequests.entries()) {
		if (!request.controller.signal.aborted) {
			request.controller.abort();
		}
		activeRequests.delete(key);
	}
};

// Add request cancellation link
const cancellationLink = new ApolloLink((operation, forward) => {
	// Create a new AbortController for this request
	const controller = new AbortController();

	// Only handle cancellation for queries, not for mutations or subscriptions
	const operationDef = operation.query.definitions[0] as OperationDefinitionNode;
	if (operationDef.operation === 'query') {
		const operationName = operation.operationName;
		const variables = JSON.stringify(operation.variables);
		const requestKey = `${operationName}-${variables}`;

		// Abort all existing requests with the same operation name
		for (const [key, request] of activeRequests.entries()) {
			if (key.startsWith(operationName) && !request.controller.signal.aborted) {
				request.controller.abort();
				activeRequests.delete(key);
			}
		}

		// Create a promise for this request
		const promise = forward(operation).map((result) => {
			const operationDef = operation.query.definitions[0] as OperationDefinitionNode;
			if (operationDef.operation === 'query') {
				const operationName = operation.operationName;
				const variables = JSON.stringify(operation.variables);
				const requestKey = `${operationName}-${variables}`;
				activeRequests.delete(requestKey);
			}
			return result;
		});

		// Store the controller and promise
		activeRequests.set(requestKey, {
			controller,
			timestamp: Date.now(),
			promise,
		});

		// Clean up old requests (older than 1 minute)
		const now = Date.now();
		for (const [key, request] of activeRequests.entries()) {
			if (now - request.timestamp > 60 * 1000) {
				if (!request.controller.signal.aborted) {
					request.controller.abort();
				}
				activeRequests.delete(key);
			}
		}

		return promise;
	}

	// Add the signal to the operation context
	operation.setContext({
		...operation.getContext(),
		fetchOptions: {
			...operation.getContext().fetchOptions,
			signal: controller.signal,
		},
	});

	return forward(operation);
});

// Add cleanup on page unload
if (typeof window !== 'undefined') {
	window.addEventListener('beforeunload', abortAllRequests);
}

const getClient = (token: string | null = null): ApolloClient<NormalizedCacheObject> => {
	const authLink = new ApolloLink((operation, forward) => {
		if (token) {
			operation.setContext(({ headers }: { headers: Record<string, unknown> }) => ({
				headers: {
					...headers,
					...{ Authorization: `Bearer ${token}` },
				},
			}));
		}

		return forward(operation);
	});

	return new ApolloClient({
		...APOLLO_CLIENT_DEFAULT_OPTIONS,
		ssrMode: typeof window === 'undefined',
		link: from([performanceLink, deduplicationLink, errorLink, cancellationLink, authLink, httpLink]),
		typeDefs,
		cache,
		defaultOptions: {
			watchQuery: {
				fetchPolicy: 'network-only' as FetchPolicy,
				notifyOnNetworkStatusChange: true,
			},
			query: {
				fetchPolicy: 'network-only' as FetchPolicy,
			},
		},
	});
};

export default getClient;
